import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  getKeywordsByRestaurant,
  getKeywordsByLanguage,
  getKeywordStats,
  KeywordCluster,
  Keyword
} from '@/data/mocks/keywords';
import { restaurants, getRestaurantById } from '@/data/mocks/restaurants';
import { useSelectedRestaurant } from '@/hooks/useSelectedRestaurant';
import {
  Search,
  Globe,
  TrendingUp,
  Filter,
  ArrowUpDown,
  BarChart,
  Languages,
  Target,
  Layers
} from 'lucide-react';

export const KeywordsPage: React.FC = () => {
  const { selectedRestaurant, setSelectedRestaurant } = useSelectedRestaurant();
  const [selectedLanguage, setSelectedLanguage] = useState<'fr' | 'en' | 'es' | 'pt'>('fr');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Keyword;
    direction: 'asc' | 'desc';
  }>({ key: 'volume', direction: 'desc' });
  const [filterIntent, setFilterIntent] = useState<string>('all');

  const currentRestaurant = getRestaurantById(selectedRestaurant);
  const keywordClusters = getKeywordsByLanguage(selectedRestaurant, selectedLanguage);
  const stats = getKeywordStats(selectedRestaurant);

  // Aplatir les clusters pour le tri et le filtrage
  const flatKeywords = useMemo(() => {
    return keywordClusters.flatMap((cluster: KeywordCluster) => 
      cluster.keywords.map((keyword: Keyword) => ({
        ...keyword,
        clusterName: cluster.name,
        clusterPriority: cluster.priority,
        clusterStatus: cluster.status
      }))
    );
  }, [keywordClusters]);

  // Filtrage
  const filteredKeywords = useMemo(() => {
    return flatKeywords.filter((keyword: any) => 
      filterIntent === 'all' || keyword.intent === filterIntent
    );
  }, [flatKeywords, filterIntent]);

  // Tri
  const sortedKeywords = useMemo(() => {
    return [...filteredKeywords].sort((a: any, b: any) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal === undefined || bVal === undefined) return 0;
      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredKeywords, sortConfig]);

  const handleSort = (key: keyof Keyword) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getOpportunityBadgeVariant = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'default';
  };

  const getKDBadgeVariant = (score: number) => {
    if (score <= 30) return 'success';
    if (score <= 60) return 'warning';
    return 'danger';
  };

  return (
    <div className="space-y-8">
      {/* En-tête avec sélecteur d'établissement */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analyse des Mots-clés</h1>
          <p className="text-gray-600 mt-1">Opportunités multilingues EN/ES/PT</p>
        </div>
        <div className="flex gap-2">
          {Object.values(restaurants).map((restaurant) => (
            <Button
              key={restaurant.id}
              variant={selectedRestaurant === restaurant.id ? "default" : "outline"}
              onClick={() => setSelectedRestaurant(restaurant.id)}
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              {restaurant.name}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Statistiques globales */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Total Mots-clés</p>
                <p className="text-2xl font-bold">{stats.totalKeywords}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Search className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Volume Total</p>
                <p className="text-2xl font-bold">{stats.totalVolume.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <BarChart className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">KD Moyen</p>
                <p className="text-2xl font-bold">{stats.avgKD}/100</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Opportunité</p>
                <p className="text-2xl font-bold">{stats.avgOpportunity}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Onglets par langue */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Tabs defaultValue="fr" onValueChange={(value) => setSelectedLanguage(value as 'fr' | 'en' | 'es' | 'pt')}>
          <TabsList className="w-full justify-start">
            <TabsTrigger value="fr" className="flex items-center gap-2">
              <Languages className="w-4 h-4" />
              Français ({stats.byLanguage.fr})
            </TabsTrigger>
            <TabsTrigger value="en" className="flex items-center gap-2">
              <Languages className="w-4 h-4" />
              English ({stats.byLanguage.en})
            </TabsTrigger>
            <TabsTrigger value="es" className="flex items-center gap-2">
              <Languages className="w-4 h-4" />
              Español ({stats.byLanguage.es})
            </TabsTrigger>
            <TabsTrigger value="pt" className="flex items-center gap-2">
              <Languages className="w-4 h-4" />
              Português ({stats.byLanguage.pt})
            </TabsTrigger>
          </TabsList>

          {/* Contenu des onglets */}
          {['fr', 'en', 'es', 'pt'].map((lang) => (
            <TabsContent key={lang} value={lang}>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Mots-clés {lang.toUpperCase()}</CardTitle>
                      <CardDescription>
                        {filteredKeywords.length} mots-clés trouvés
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-4">
                      <select
                        value={filterIntent}
                        onChange={(e) => setFilterIntent(e.target.value)}
                        className="px-3 py-2 border rounded-md text-sm"
                      >
                        <option value="all">Tous les intents</option>
                        <option value="commercial">Commercial</option>
                        <option value="informational">Informationnel</option>
                        <option value="navigational">Navigation</option>
                        <option value="transactional">Transactionnel</option>
                      </select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cluster</TableHead>
                          <TableHead>
                            <Button
                              variant="ghost"
                              onClick={() => handleSort('query')}
                              className="flex items-center gap-1"
                            >
                              Query
                              <ArrowUpDown className="w-4 h-4" />
                            </Button>
                          </TableHead>
                          <TableHead>Intent</TableHead>
                          <TableHead>
                            <Button
                              variant="ghost"
                              onClick={() => handleSort('volume')}
                              className="flex items-center gap-1"
                            >
                              Volume
                              <ArrowUpDown className="w-4 h-4" />
                            </Button>
                          </TableHead>
                          <TableHead>
                            <Button
                              variant="ghost"
                              onClick={() => handleSort('kd')}
                              className="flex items-center gap-1"
                            >
                              KD
                              <ArrowUpDown className="w-4 h-4" />
                            </Button>
                          </TableHead>
                          <TableHead>SERP Features</TableHead>
                          <TableHead>
                            <Button
                              variant="ghost"
                              onClick={() => handleSort('opportunity')}
                              className="flex items-center gap-1"
                            >
                              Opportunité
                              <ArrowUpDown className="w-4 h-4" />
                            </Button>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedKeywords.map((keyword) => (
                          <TableRow key={keyword.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Layers className="w-4 h-4 text-gray-500" />
                                <span>{keyword.clusterName}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="font-medium">{keyword.query}</div>
                                {keyword.position && (
                                  <div className="text-sm text-gray-500">
                                    Position: #{keyword.position}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{keyword.intent}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">
                                {keyword.volume.toLocaleString()}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={getKDBadgeVariant(keyword.kd)}>
                                {keyword.kd}/100
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 flex-wrap">
                                {keyword.serpFeatures.map((feature, index) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {feature}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={getOpportunityBadgeVariant(keyword.opportunity)}>
                                {keyword.opportunity}/100
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </motion.div>
    </div>
  );
};